import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchBans, unbanIp, type BanEntry } from '../api/security';
import { usePermission } from '../hooks/usePermission';
import { useConfirm } from '../context/ConfirmDialogContext';
import { useSnackbar } from '../context/SnackbarContext';

export function Security() {
  const qc = useQueryClient();
  const canRead = usePermission('security.read');
  const canManage = usePermission('security.manage');
  const confirm = useConfirm();
  const showSnackbar = useSnackbar();

  const { data: bans, isLoading, error } = useQuery({
    queryKey: ['security-bans'],
    queryFn: fetchBans,
    enabled: canRead,
    refetchInterval: 30_000,
  });

  const unbanMutation = useMutation({
    mutationFn: (ip: string) => unbanIp(ip),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['security-bans'] }),
  });

  async function handleUnban(ip: string) {
    const ok = await confirm(`Remove the active ban for ${ip}?`, {
      title: 'Unban IP',
      confirmLabel: 'Unban',
      danger: false,
    });
    if (!ok) return;
    try {
      await unbanMutation.mutateAsync(ip);
      showSnackbar(`Unbanned ${ip}`, 'success');
    } catch {
      showSnackbar('Failed to unban IP', 'error');
    }
  }

  const columns: GridColDef<BanEntry>[] = [
    { field: 'ip', headerName: 'IP Address', width: 160, renderCell: (p: GridRenderCellParams<BanEntry>) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value as string}</Typography>
    )},
    { field: 'reason', headerName: 'Reason', width: 180, renderCell: (p: GridRenderCellParams<BanEntry>) => (
      <Chip label={(p.value as string).replace('_', ' ')} size="small" color="error" />
    )},
    { field: 'failed_attempts', headerName: 'Attempts', width: 100 },
    { field: 'banned_at', headerName: 'Banned At', width: 180,
      valueGetter: (_v, row) => new Date(row.banned_at).toLocaleString() },
    { field: 'until', headerName: 'Banned Until', width: 180,
      valueGetter: (_v, row) => new Date(row.until).toLocaleString() },
    ...(canManage ? [{
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (p: GridRenderCellParams<BanEntry>) => (
        <Button
          size="small"
          startIcon={<LockOpenIcon />}
          onClick={() => handleUnban(p.row.ip)}
          disabled={unbanMutation.isPending}
        >
          Unban
        </Button>
      ),
    } as GridColDef<BanEntry>] : []),
  ];

  if (!canRead) {
    return <Typography color="error">You do not have permission to view security bans.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Security — Active IP Bans</Typography>

      {isLoading && <CircularProgress />}
      {error && <Typography color="error">Failed to load bans</Typography>}
      {bans && bans.length === 0 && (
        <Typography color="text.secondary">No active IP bans.</Typography>
      )}
      {bans && bans.length > 0 && (
        <DataGrid
          rows={bans}
          columns={columns}
          getRowId={(r) => r.ip}
          density="compact"
          disableRowSelectionOnClick
          pageSizeOptions={[25]}
          sx={{ bgcolor: 'background.paper' }}
        />
      )}
    </Box>
  );
}
